import { useNavigate, useSearchParams } from "react-router-dom";
import React, {
  createContext,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  MeetingModeEnum,
  ReviewFormData,
  TimeSlotRange,
} from "pages/CalendarsPage/SchedulingSection/form.types";
import dayjs from "@lib/dayjs";
import { createInterview } from "endpoints/interview.service";
import { useSnackbar } from "notistack";
import LoadingAnimation from "assets/svg/LoadingAnimation";
import { IJobMeetingRequirements } from "@type/hiringStaff.type";
import InviteTypeDialog from "./components/InviteTypeDialog";
import { isObjectEmpty } from "@utils/helperFunction";
import MeetingDateSelectionSection from "./sections/MeetingDateSelectionSection";
import AttendeeSection from "./sections/AttendeeSection";
import MeetingGeneralInfoSection from "./sections/MeetingGeneralInfoSection";
import ReviewMeetingPoll from "./sections/ReviewMeetingPoll";
import { getMeetingRequirements } from "@endpoints/hiringCompany/requirements";
import { CalendarContext } from "@pages/CalendarsPage/MeetingLayout";

export function availabilitySlotsForApiPayload(
  slots: Record<string, TimeSlotRange[]>
) {
  const finalSlots = [];
  Object.entries(slots).forEach(([key, timeSlots]) => {
    const date = key;
    timeSlots?.forEach((timeSlot) => {
      finalSlots.push({
        startTime: dayjs(timeSlot?.startTime)
          .set("year", dayjs(date).year())
          .set("month", dayjs(date).month())
          .set("date", dayjs(date).date())
          .toISOString(),
        endTime: dayjs(timeSlot?.endTime)
          .set("year", dayjs(date).year())
          .set("month", dayjs(date).month())
          .set("date", dayjs(date).date())
          .toISOString(),
      });
    });
  });

  return finalSlots;
}

export enum MeetingSectionEnum {
  REVIEW = "review",
  ATTENDEE = "attendee",
  DATE_SELECTION = "dateSelection",
}

export const MeetingContext = createContext<{
  meetingRequirements?: IJobMeetingRequirements;
  setMeetingRequirements: React.Dispatch<
    SetStateAction<IJobMeetingRequirements | undefined>
  >;
  formData: ReviewFormData;
  setFormData?: React.Dispatch<SetStateAction<ReviewFormData>>;
  fetchMeetingRequirements?: (value: boolean) => void;
} | null>(null);

const CreateMeetingPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const meetingGeneralInfoRef = useRef(null);
  const meetingDateSelectionRef = useRef(null);
  const attendeeRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Local state
  const [events, setEvents] = useState({});
  const [activeWeeks, setActiveWeeks] = useState();
  const [showCreateEventFormModal, setShowCreateEventFormModal] =
    useState(false);
  const [formData, setFormData] = useState<ReviewFormData>({});
  const [openDialog, setOpenDialog] = useState(true);
  const [isInterviewCreating, setIsInterviewCreating] = useState(false);
  const [meetingRequirements, setMeetingRequirements] =
    useState<IJobMeetingRequirements>();

  const section = searchParams.get("section");
  const isMeetingInvite = searchParams.get("type") === "invite";

  const params = useMemo(() => {
    const jobId = searchParams.get("jobId");
    const candidateId = searchParams.get("candidateId");
    const jobRequirementId = searchParams.get("jobRequirementId");

    return { jobId, candidateId, jobRequirementId };
  }, [searchParams]);

  const addParam = (key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(key, value);
      return next;
    });
  };

  const isDateSelectionSection = section === MeetingSectionEnum.DATE_SELECTION;
  const isAttendeeSection = section === MeetingSectionEnum.ATTENDEE;
  const isReviewSection = section === MeetingSectionEnum.REVIEW;

  async function initLocalStorage() {
    const hasFlowParams = Boolean(
      section || params?.jobId || params?.candidateId || params?.jobRequirementId
    );

    // Fresh create flow should never inherit an old draft.
    if (!hasFlowParams) {
      window.localStorage.removeItem("createMeetingData");
      setFormData({});
      return;
    }

    const storageMeetingData = await JSON.parse(
      window.localStorage.getItem("createMeetingData")
    );
    if (!isObjectEmpty(storageMeetingData)) {
      setFormData(storageMeetingData);
    }
  }

  const handleNext = async () => {
    if (!section) {
      try {
        const data = await meetingGeneralInfoRef.current?.submit();
        setFormData((prev) => {
          return { ...prev, ...data };
        });
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set("section", MeetingSectionEnum.ATTENDEE);
          if (data?.candidateDetails?.candidateId) {
            next.set("candidateId", data.candidateDetails.candidateId);
          }
          if (data?.jobDetails?.jobId) {
            next.set("jobId", data.jobDetails.jobId);
          }
          if (data?.jobDetails?.jobRequirementId) {
            next.set("jobRequirementId", data.jobDetails.jobRequirementId);
          }
          return next;
        });
      } catch (error) {
        console.log("Form invalid", error);
      }
    } else if (isAttendeeSection) {
      try {
        const data = attendeeRef.current?.submit();
        const requirementTeamMembers =
          meetingRequirements?.jobRequirementTeamMembers || [];

        const resolvedUserIds = (data?.selectedHiringMembers || [])
          .map((item): string | undefined => {
            const matchedMember = requirementTeamMembers.find(
              (member) =>
                member?._id?.toString() === item?._id?.toString() ||
                member?.userId?.toString() === item?.userId?.toString()
            );
            const resolvedUserId = matchedMember?.userId || item?.userId;
            return resolvedUserId ? resolvedUserId.toString() : undefined;
          })
          .filter((id): id is string => Boolean(id));

        const hiringTeamMembers = Array.from(new Set<string>(resolvedUserIds)).map(
          (userId) => ({ userId })
        );
        // if (!hiringTeamMembers?.length) {
        //   enqueueSnackbar("You must select at least 1 hiring member.", {
        //     variant: "error",
        //   });
        //   return;
        // }
        setFormData((prev) => {
          return {
            ...prev,
            hiringTeamMembers,
            isShareToOnlyHiringTeamMember:
              data?.isShareToOnlyHiringTeamMember || false,
          };
        });
        console.log("hiring members", data);
        // return;
        addParam("section", MeetingSectionEnum.DATE_SELECTION);
      } catch (error) {
        console.log("Form invalid", error);
      }
    } else if (isDateSelectionSection) {
      try {
        const data = await meetingDateSelectionRef.current?.submit();
        setFormData((prev) => {
          return { ...prev, ...data };
        });
        addParam("section", MeetingSectionEnum.REVIEW);
      } catch (error) {
        console.log("Form invalid", error);
      }
    } else if (isReviewSection) {
      try {
        setIsInterviewCreating(true);
        const apiPayload = {
          candidateId: formData?.candidateDetails?.candidateId,
          jobId: formData?.jobDetails?.jobId,
          meetingTitle: formData?.meetingTitle,
          meetingDescription: formData?.meetingDescription,
          meetingDuration: formData?.meetingDuration?.duration,
          meetingMode: MeetingModeEnum[formData?.meetingMode],
          slotSelectionType: isMeetingInvite ? "specific" : "general",
          hiringTeamMembers: formData?.hiringTeamMembers,
          isShareToOnlyHiringTeamMember:
            formData?.isShareToOnlyHiringTeamMember || false,
        };
        if (isMeetingInvite) {
          apiPayload["specificSlot"] = availabilitySlotsForApiPayload(
            formData?.availabilitySlots
          )[0];
        } else {
          apiPayload["generalAvailabilitySlots"] =
            availabilitySlotsForApiPayload(formData?.availabilitySlots);
        }
        if (formData?.meetingMode === MeetingModeEnum["Virtual Meeting"]) {
          apiPayload["url"] = formData?.url;
        } else if (formData?.meetingMode === MeetingModeEnum["Face-to-Face"]) {
          apiPayload["address"] = formData?.address;
        } else if (
          formData?.meetingMode === MeetingModeEnum["Telephone Call"]
        ) {
          apiPayload["phone"] = formData?.phone;
        }
        console.log("api payload", apiPayload);
        // return;
        const { success, data } = await createInterview(apiPayload);
        if (success) {
          window.localStorage.removeItem("createMeetingData");
          enqueueSnackbar(
            `${isMeetingInvite ? "Invite" : "Poll"} sent successfully.`,
            {
              variant: "success",
            }
          );
          navigate(`/meeting-details?id=${data?._id}`);
          // if (data?.slotSelectionType === "general") {
          //   navigate("/calendar/meeting/scheduling");
          // } else {
          //   navigate("/calendar");
          // }
        }
        console.log("success data", success);
        // const apiPayload = await reviewMeetingPollRef.current?.submit();
        // console.log("api payload", apiPayload, formData);
      } catch (error) {
        console.log("Error creating meeting", error);
      } finally {
        setIsInterviewCreating(false);
      }
    }
  };

  const handleBack = () => {
    if (!section) {
      navigate("/calendar/meeting/schedules");
    } else if (isAttendeeSection) {
      navigate(
        `/calendar/meeting-invitation?jobId=${params?.jobId}&candidateId=${params?.candidateId}&jobRequirementId=${params?.jobRequirementId}`
      );
    } else if (isDateSelectionSection) {
      addParam("section", MeetingSectionEnum.ATTENDEE);
    } else if (isReviewSection) {
      addParam("section", MeetingSectionEnum.DATE_SELECTION);
    }
  };

  async function fetchMeetingRequirements(
    updateHiringTeamMembers: boolean = false
  ) {
    const { jobId, jobRequirementId, candidateId } = params;
    if (!jobId || !jobRequirementId || !candidateId) return;

    const { success, data } = await getMeetingRequirements({
      jobRequirementId,
      jobId,
      candidateId,
    });
    if (success && !updateHiringTeamMembers) {
      setMeetingRequirements(data);
    } else if (success && updateHiringTeamMembers) {
      setMeetingRequirements((prev) => {
        return {
          ...prev,
          jobRequirementTeamMembers: data?.jobRequirementTeamMembers,
        };
      });
    }
  }

  useEffect(() => {
    if (!isObjectEmpty(formData)) {
      window.localStorage.setItem(
        "createMeetingData",
        JSON.stringify(formData)
      );
    }
  }, [formData]);

  useEffect(() => {
    initLocalStorage();
  }, [section, params?.jobId, params?.candidateId, params?.jobRequirementId]);

  useEffect(() => {
    if (params) {
      fetchMeetingRequirements();
    }
  }, [params]);

  console.log("meeting requirements", meetingRequirements, formData);

  return (
    <CalendarContext.Provider
      value={{
        activeWeeks,
        setActiveWeeks,
        events,
        setEvents,
        showCreateEventFormModal,
        setShowCreateEventFormModal,
      }}
    >
      <MeetingContext.Provider
        value={{
          meetingRequirements,
          setMeetingRequirements,
          formData,
          fetchMeetingRequirements,
        }}
      >
        <div className="bg-white rounded-md w-full p-4">
          {/* Inner Wrapper */}
          <div
            className={`flex flex-col rounded-lg w-full p-3 items-center h-[calc(100vh-120px)]`}
          >
            <div
              className={`${
                isDateSelectionSection || isAttendeeSection || isReviewSection
                  ? "w-full"
                  : "w-[65%]"
              } flex flex-col flex-1 min-h-0`}
            >
              {/* Header */}
              <div className="flex items-center justify-between w-full flex-shrink-0">
                <button
                  onClick={handleBack}
                  className="bg-white border-primary border-2 rounded-md text-primary font-semibold text-sm py-1.5 px-6 hover:bg-gray-50"
                >
                  Back
                </button>
                <span className="text-primary font-semibold text-lg">
                  {isAttendeeSection ? (
                    "Attendee"
                  ) : (
                    <>
                      {isReviewSection ? "Review" : "Schedule"}{" "}
                      {isMeetingInvite ? "Invite" : "Availability Poll"}
                    </>
                  )}
                </span>
                <button
                  onClick={handleNext}
                  className={`bg-primary rounded-md text-white font-semibold text-sm py-2 ${
                    isInterviewCreating ? "pl-6" : "px-6"
                  } flex items-center gap-4`}
                >
                  {isReviewSection
                    ? isMeetingInvite
                      ? "Send Invite"
                      : "Send Poll"
                    : "Next"}
                  {isInterviewCreating ? <LoadingAnimation /> : null}
                </button>
              </div>

              {/* Main components */}
              {isReviewSection ? (
                <ReviewMeetingPoll
                  formData={formData}
                  isMeetingInvite={isMeetingInvite}
                />
              ) : isDateSelectionSection ? (
                <MeetingDateSelectionSection
                  ref={meetingDateSelectionRef}
                  formData={formData}
                />
              ) : isAttendeeSection ? (
                <AttendeeSection ref={attendeeRef} />
              ) : (
                <MeetingGeneralInfoSection
                  ref={meetingGeneralInfoRef}
                  formData={formData}
                />
              )}
            </div>
          </div>

          <InviteTypeDialog
            openDialog={openDialog}
            setOpenDialog={setOpenDialog}
            onChange={(value) => {
              if (value === "invite") {
                addParam("type", "invite");
              } else {
                addParam("type", "meetingPoll");
              }
            }}
          />
        </div>
      </MeetingContext.Provider>
    </CalendarContext.Provider>
  );
};

export default CreateMeetingPage;
